/**
 * Was ein Bot tut, wenn seine Linie gedrueckt wird (09.10.2026).
 *
 * Die Frage "werde ich zurueckgedraengt?" beantwortet er NICHT selbst -
 * er benutzt dieselbe Regel wie die Warnung im HUD (druckGruende). So
 * reagiert er genau dann, wenn ein Mensch die Warnung sehen wuerde.
 * Alle Zahlen hier sind TUNING_PLACEHOLDER.
 */
export interface BotFrontReaktion {
  /**
   * Ab welchem Vordringen des Gegners er aufwacht, als Anteil der Lane.
   *
   * Der starke Bot nimmt die Schwelle des Menschen; ein mittlerer merkt
   * es spaeter.
   */
  schwelle: number;
  /**
   * Hoert er unter Druck auf, fuer den naechsten Tier zu sparen?
   *
   * Das Sparen drosselt seine Wellen auf drei Fuenftel. Wer gedrueckt
   * wird, braucht die Welle jetzt - ein Tier in zwei Minuten nuetzt
   * nichts, wenn das Anchor vorher faellt.
   */
  sparenAussetzen: boolean;
  /**
   * Antwortet er dann mit Gegendruck (viele billige Koerper)?
   *
   * Zuerst war es das Gegenteil - eine Sperrwelle aus zaehen Creeps.
   * Gemessen stieg damit die Zahl der Matches ohne Portalsieg von 1,4
   * auf 11,8 Prozent: Mauern beide Seiten, mahlt sich die Front fest.
   * Gewonnen wird am GEGNERISCHEN Anchor, nicht an der eigenen Linie.
   *
   * Ein Konter gegen Laeufer geht trotzdem vor.
   */
  gegendruck: boolean;
}
/**
 * Testgegner (Masterprompt Abschnitt 97).
 *
 * Der Bot spielt ein vollstaendiges Match: Er wirtschaftet, schickt Wellen,
 * kauft Defender und Items und steuert seinen Hero.
 *
 * Produktentscheidung vom 24.09.2026: drei waehlbare Staerken. Sie
 * unterscheiden sich in Reaktionszeit, Sorgfalt beim Geldausgeben und
 * Aggressivitaet - nicht in erfundenen Boni. Ein Bot, der mehr Gold bekaeme
 * als ein Mensch, wuerde nichts ueber die Balance verraten.
 */

import { FRONTLINE_PRESSURE_SHARE, HERO_FRONTLINE_ZONE_DEPTH } from './match.js';

export type BotDifficulty = 'easy' | 'normal' | 'hard';

export interface BotProfile {
  id: BotDifficulty;
  name: string;
  /**
   * Wie viele Sekunden zwischen zwei Entscheidungen liegen.
   *
   * Das ist der wichtigste Regler: Ein langsamer Bot laesst Gold liegen und
   * reagiert spaeter auf eine Welle.
   */
  decisionSeconds: number;
  /** Anteil des Goldes, den er hoechstens in eine Welle steckt. */
  waveGoldRatio: number;
  /**
   * Anteil des Army-Point-Caps, den er in einer Welle ausnutzen darf
   * (Produktentscheidung vom 05.10.2026).
   *
   * Der wichtigste Regler, seit gemessen ist, dass die anderen nichts tun:
   * Am 05.10.2026 kamen Leicht und Schwer nach sechs Minuten auf dasselbe
   * Tier, dieselben zwoelf Wellen, dieselben vier Items und ein Prozent
   * Unterschied beim Gold.
   *
   * Der Grund: Der Engpass ist nicht das Gold, sondern der Wellentakt von
   * 20 Sekunden und das Punkte-Cap. Wer alle 6 Sekunden entscheidet,
   * erwischt jedes 20-Sekunden-Fenster genauso sicher wie einer, der alle
   * 1,5 Sekunden entscheidet - und wer 90 Prozent seines Goldes in die
   * Welle stecken DARF, kann es nicht, weil die Punkte vorher alle sind.
   *
   * Dieser Anteil greift direkt an der bindenden Grenze an und bestimmt
   * damit unmittelbar, wie gross die Welle wird, die beim Spieler ankommt.
   *
   * "hard" steht bewusst auf 1: Der Massentest laeuft gegen diese Stufe,
   * und alle bisherigen Messungen sollen vergleichbar bleiben.
   */
  armyPointShare: number;
  /**
   * Anteil des Goldes, der fuer die naechste Welle zurueckliegt
   * (Produktentscheidung vom 29.09.2026).
   *
   * Traenke, Defender und Items duerfen ihn nicht anfassen. Der Tier-Aufstieg
   * schon: Er ist die Voraussetzung fuer groessere Wellen und damit kein
   * Gegner davon - und er hat mit isSavingForTier schon eine eigene Bremse.
   *
   * Warum es das braucht: Mit Wellentakt 20 Sekunden wuchs die Welle nur von
   * 2,06 auf 2,25 Creeps, obwohl doppelt so viel Zeit zum Ansparen da war. Die
   * Entscheidungsschleife laeuft alle 1,5 Sekunden - die anderen Posten
   * greifen dreizehnmal zu, bevor die Welle dran ist.
   *
   * Wie bei itemGoldShare gilt die Ruecklage erst ab itemGoldThreshold. Wer
   * 200 Gold hat, legt nichts zurueck: Sonst kann er sich in den ersten zwei
   * Minuten keinen einzigen Defender kaufen und verliert sein Portal.
   *
   * Die Disziplin gehoert zur Staerke: Ein Easy-Bot spart weniger an und
   * schickt kleinere Wellen.
   */
  waveGoldShare: number;
  /**
   * Gold, das nach einem Tier-Aufstieg noch uebrig bleiben soll.
   *
   * Vorsicht mit grossen Werten: Zusammen mit den Aufstiegskosten bestimmt er,
   * ab wann der Bot ueberhaupt aufsteigt. Im ersten Testlauf stand er deshalb
   * nach vier Minuten immer noch auf Tier I.
   */
  tierReserve: number;
  /**
   * Ab so viel Gold legt er ueberhaupt etwas zurueck - fuer Items UND fuer
   * die naechste Welle.
   *
   * Darunter ist alles Gold frei: Wer 200 Gold hat, muss sich verteidigen
   * koennen, statt auf etwas zu sparen, das er sich ohnehin nicht leisten kann.
   */
  itemGoldThreshold: number;
  /**
   * Anteil des Goldes, der fuer Ausruestung zurueckgelegt ist.
   *
   * Produktentscheidung vom 28.09.2026: Dieser Teil steht dem Wellenbudget
   * NICHT zur Verfuegung, und gekauft wird nur, wenn er den Preis deckt.
   *
   * Vorher kaufte der Bot praktisch nie ein Item: Die Welle nahm sich bis zu
   * 90 Prozent des Goldes, und das Tier-Sparen brach den Einkauf ganz ab.
   * Gemessen ueber 720 Matches kam ein Item auf 2 bis 8 Prozent der Bots - bei
   * 46.000 Gold Einnahmen.
   *
   * Ein Anteil und kein festes Konto: So skaliert die Ruecklage mit dem
   * Einkommen, und der Tier-Aufstieg bleibt moeglich, weil der Rest weiter
   * bereitliegt.
   */
  itemGoldShare: number;
  /** Wie wahrscheinlich er einen bereiten Skill auch wirklich einsetzt. */
  skillChance: number;
  /**
   * Wie weit er mit dem Hero vorrueckt, als Anteil der Lane.
   * 0 = klebt am Portal, 1 = laeuft bis zum Creep-Spawn.
   */
  pushRatio: number;
  /**
   * Ab welchem Anteil schneller Gegner er mit Fernkaempfern antwortet
   * (Produktentscheidung vom 08.10.2026).
   *
   * Daran haengt die Schwierigkeit: Ein starker Bot sieht eine
   * Laeuferwelle frueh kommen, ein schwacher erst, wenn sie ihn
   * ueberrollt. Bewusst eine Schwelle und kein Wuerfel - so bleibt jedes
   * Match vergleichbar.
   */
  counterThreshold: number;
  /**
   * Ab so viel Gold gilt sein Besitz als Ueberschuss, und er steckt mehr
   * davon in die Welle (Produktentscheidung vom 08.10.2026).
   *
   * Gemessen blieb er vorher auf 35 Prozent seines Goldes sitzen - das war
   * das Defender-Budget, das mit dem Defense System weggefallen ist.
   */
  goldSurplusFrom: number;
  /** Unter diesem Anteil Leben zieht er sich zum Portal zurueck. */
  retreatHpRatio: number;

  /**
   * Wie er darauf reagiert, an der Front zurueckgedraengt zu werden
   * (Produktentscheidung vom 09.10.2026, GAME_DESIGN Abschnitt 5a).
   *
   * Bis dahin kannte er die Frontline ueberhaupt nicht: Er schickte
   * Wellen nach Kassenlage und sparte stur auf den naechsten Tier -
   * auch dann noch, wenn der Gegner schon vor seinem Anchor stand.
   * Damit benutzte er die zentrale Regel der gemeinsamen Lane nicht,
   * und jede Messung daran mass etwas anderes (CLAUDE.md, Lehre 2).
   *
   * null heisst: Er merkt es nicht. Das bleibt beim leichten Bot so -
   * er soll sich ueberrennen lassen.
   */
  front: BotFrontReaktion | null;

  /**
   * Wann dieser Bot den gegnerischen HERO angreift
   * (Produktentscheidung vom 09.10.2026, GAME_DESIGN Abschnitt 5b).
   *
   * Bis dahin griff er ihn nie an - er wehrte sich nur, wenn er selbst
   * getroffen wurde. Als Uebungsgegner war das duenn, und gemessen liess
   * sich ueber PvP gar nichts: Was der Testgegner nie anfasst, taucht in
   * keiner Statistik auf.
   *
   * Drei Anlaesse, gestaffelt ueber die drei Staerken. Keiner davon
   * macht Hero gegen Hero zum Siegweg - ein Hero-Kill bringt dem Bot
   * genauso wenig ein wie einem Menschen.
   */
  pvp: {
    /**
     * Nimmt ihn mit, wenn er ohnehin in Angriffsreichweite steht.
     *
     * Kostet nichts: Der Bot geht keinen Schritt dafuer vom Weg ab, er
     * schlaegt nur auf das, was vor ihm steht.
     */
    gelegenheit: boolean;
    /**
     * Geht bewusst auf ihn los, wenn er SO TIEF in der eigenen Kampfzone
     * steht - gemessen vor der eigenen Frontline. null heisst: nie.
     *
     * Das bestraft genau das, was die groessere Kampfzone seit dem
     * 08.10.2026 erlaubt: sich zu weit vorzuwagen.
     */
    strafeAbTiefe: number | null;
    /**
     * Setzt nach, wenn der gegnerische Hero unter diesem Anteil Leben
     * faellt. 0 heisst: nie.
     */
    finishHpRatio: number;
    /**
     * Wie oft ein Skill bewusst auf den gegnerischen HERO geht statt auf
     * die Welle. 0 heisst: nie.
     *
     * Rueckmeldung vom 09.10.2026: "nicht als primaeres Ziel, aber
     * irgendwie halt auch mal bewusst auf ihn". Genau deshalb eine
     * Wahrscheinlichkeit und keine Bedingung - der Bot soll ab und zu
     * einen Zauber an ihn verschwenden, nicht seine Welle vergessen.
     *
     * Das Ultimate ist davon ausgenommen: Es gehoert der Welle.
     * TUNING_PLACEHOLDER.
     */
    skillChance: number;
  };
}

export const BOT_PROFILES: Readonly<Record<BotDifficulty, BotProfile>> = {
  easy: {
    id: 'easy',
    name: 'Easy',
    decisionSeconds: 6,
    waveGoldRatio: 0.5,
    armyPointShare: 0.5,
    waveGoldShare: 0.3,
    tierReserve: 300,
    itemGoldThreshold: 1500,
    itemGoldShare: 0.15,
    skillChance: 0.3,
    pushRatio: 0.25,
    counterThreshold: 0.5,
    goldSurplusFrom: 4000,
    retreatHpRatio: 0.5,
    // Easy laesst den Spieler in Ruhe - er wehrt sich nur, wenn er
    // selbst getroffen wird. Das ist der Gegner zum Ausprobieren.
    pvp: { gelegenheit: false, strafeAbTiefe: null, finishHpRatio: 0, skillChance: 0 },
    // Der leichte Bot merkt den Druck gar nicht - er soll sich ueberrennen lassen.
    front: null,
  },
  normal: {
    id: 'normal',
    name: 'Normal',
    decisionSeconds: 3,
    waveGoldRatio: 0.7,
    armyPointShare: 0.75,
    waveGoldShare: 0.45,
    tierReserve: 100,
    itemGoldThreshold: 900,
    itemGoldShare: 0.25,
    skillChance: 0.6,
    pushRatio: 0.45,
    counterThreshold: 0.3,
    goldSurplusFrom: 2500,
    retreatHpRatio: 0.35,
    // Normal nimmt mit, was vor ihm steht - macht aber keinen Umweg.
    pvp: { gelegenheit: true, strafeAbTiefe: null, finishHpRatio: 0, skillChance: 0 },
    /*
     * Er merkt es spaet und reagiert nur mit Geld, nicht mit der
     * Zusammenstellung: Die Haelfte des Weges zwischen der Schwelle des
     * Menschen und dem eigenen Anchor.
     */
    front: {
      schwelle: FRONTLINE_PRESSURE_SHARE + (1 - FRONTLINE_PRESSURE_SHARE) / 2,
      sparenAussetzen: true,
      gegendruck: false,
    },
  },
  hard: {
    id: 'hard',
    name: 'Hard',
    decisionSeconds: 1.5,
    waveGoldRatio: 0.9,
    armyPointShare: 1,
    waveGoldShare: 0.6,
    tierReserve: 0,
    itemGoldThreshold: 500,
    itemGoldShare: 0.35,
    skillChance: 0.9,
    pushRatio: 0.6,
    counterThreshold: 0.15,
    goldSurplusFrom: 1500,
    retreatHpRatio: 0.25,
    /*
     * Hard kann alle drei. Die Tiefe ist aus der Kampfzone abgeleitet:
     * Wer eine halbe Zonentiefe VOR der Frontline des Bots steht, steht
     * mitten in dessen Armee - und das soll wehtun.
     */
    pvp: {
      gelegenheit: true,
      strafeAbTiefe: HERO_FRONTLINE_ZONE_DEPTH / 2,
      finishHpRatio: 0.25,
      // Ungefaehr jeder vierte Zauber geht an ihn, wenn er in Reichweite ist.
      skillChance: 0.25,
    },
    /*
     * Er reagiert genau dann, wenn ein Mensch die Warnung
     * "YOUR LINE IS UNDER PRESSURE" sieht - dieselbe Schwelle, dieselbe
     * Regel. Ein Testgegner, der frueher aufwacht als der Spieler,
     * waere kein Gegner mehr, sondern ein Hellseher.
     */
    front: { schwelle: FRONTLINE_PRESSURE_SHARE, sparenAussetzen: true, gegendruck: true },
  },
};

/** Alle Staerken in der Reihenfolge, in der sie im Menue stehen. */
export const BOT_DIFFICULTIES: readonly BotDifficulty[] = ['easy', 'normal', 'hard'];

export function getBotProfile(id: string): BotProfile | undefined {
  return BOT_PROFILES[id as BotDifficulty];
}

/**
 * Leben, das ein gesendeter Creep mindestens haben muss, damit der Testgegner
 * ihn ueberhaupt kauft - abhaengig von seinem eigenen Tier
 * (Produktentscheidung vom 29.09.2026).
 *
 * Warum eine Schwelle und keine Formel: Gemessen ueber 356 Matches schickte
 * ein Bot, der rein nach Portalschaden je Gold einkauft, ausschliesslich
 * Minions - 18 je Welle, davon kamen 0,1 Prozent an. Ueberleben ist ein
 * Schwellenwert. Wer die Lane nicht abschreitet, richtet nichts an, egal wie
 * billig er war.
 *
 * Die Schwelle waechst mit dem Tier, weil auch die Verteidigung waechst: Was
 * in Minute zwei durchkommt, schmilzt in Minute zwoelf sofort weg.
 *
 * Die Zahlen sind TUNING_PLACEHOLDER - sie sind aus der Creep-Tabelle
 * abgelesen, nicht gemessen. Gemessen ist nur, dass es die Schwelle BRAUCHT.
 */
export const BOT_MIN_CREEP_HP: Readonly<Record<1 | 2 | 3 | 4, number>> = {
  1: 150,
  2: 450,
  3: 850,
  4: 1100,
};

/**
 * Arten von Wellen, die der Testgegner abwechselnd schickt
 * (Produktentscheidung vom 29.09.2026).
 *
 * Warum es das braucht: Gemessen ueber vier Laeufe haengt die Staerke eines
 * Heroes vor allem daran, WAS der Gegner schickt. Der Deadeye war bei grossen,
 * teuren Creeps der beste Hero (71,4 Prozent) und bei vielen kleinen der
 * zweitschlechteste (39,7); beim Shade war es genau umgekehrt (47,5 auf 79,2).
 * Blade Whirl und Thornburst treffen alle gleichzeitig, Marked Prey und
 * Barrage zerlegen einzelne harte Ziele.
 *
 * Solange der Bot immer dasselbe schickt, misst jede "Hero-Balance" also nur,
 * welcher Hero zur Lieblingswelle des Bots passt. Ein Mensch stellt um, sobald
 * er merkt, dass der Gegner Flaechenschaden hat - der Testgegner muss das auch.
 */
export type BotWaveStyle = 'swarm' | 'mixed' | 'breaker' | 'ranged';

export interface BotWaveStyleRule {
  /**
   * Vielfaches von BOT_MIN_CREEP_HP, das ein Creep mindestens haben muss.
   *
   * 1 heisst "alles, was die Lane ueberlebt", 2 heisst "nur die Zaeheren".
   */
  minHpFactor: number;
  /**
   * Wonach er unter den Tauglichen auswaehlt.
   *
   * value     = Portalschaden je Gold. Viele, gerade noch haltbare Creeps.
   * toughness = Leben absteigend. Wenige, die kaum zu toeten sind.
   * ranged    = Fernkaempfer zuerst. Die Antwort auf Laeufer: Sie lassen
   *             sich nicht aufhalten, nur erschiessen (Abschnitt 12a).
   */
  pick: 'value' | 'toughness' | 'ranged';
}

export const BOT_WAVE_STYLES: Readonly<Record<BotWaveStyle, BotWaveStyleRule>> = {
  swarm: { minHpFactor: 1, pick: 'value' },
  mixed: { minHpFactor: 2, pick: 'value' },
  breaker: { minHpFactor: 1, pick: 'toughness' },
  /*
   * Die Lebensschwelle faellt hier auf 1: Fernkaempfer sind duenn, und mit
   * der strengeren Schwelle waeren sie gar nicht erst zur Wahl gestanden -
   * genau der Fehler, der die Laeufer schon einmal unsichtbar gemacht hat.
   */
  ranged: { minHpFactor: 1, pick: 'ranged' },
};

/**
 * Reihenfolge, in der er die Arten abwechselt.
 *
 * Eine feste Reihenfolge und kein Zufall: So ist jedes Match vergleichbar, und
 * im Archiv steht, was er geschickt hat. Zufall wuerde jedes Match zum
 * Einzelfall machen - dieselbe Ueberlegung wie bei den Bauplaenen.
 */
export const BOT_WAVE_STYLE_CYCLE: readonly BotWaveStyle[] = ['swarm', 'mixed', 'breaker'];

/**
 * So viele Gegner muessen anruecken, bevor er ueberhaupt kontert.
 *
 * Ohne diese Grenze ergaebe ein einzelner Laeufer rechnerisch 100 Prozent
 * Schnelle und wuerfe jede Welle um. TUNING_PLACEHOLDER.
 */
export const BOT_COUNTER_MIN_ENEMIES = 4;

/** Name, den der Bot als Gegner traegt. */
export const BOT_NAME = 'Practice Bot';

/**
 * Bauplan eines Bots (Produktentscheidung vom 25.09.2026).
 *
 * Der Bot wuerfelt nicht, was er ausbaut - er folgt einem von mehreren FESTEN
 * Bauplaenen. Der gewaehlte Plan steht im Matcharchiv. Dadurch laesst sich
 * spaeter auswerten, welcher Weg sich wie schlaegt; bei Zufall waere jedes
 * Match ein Einzelfall und nichts davon vergleichbar.
 *
 * Je Hero gibt es DREI Plaene, die ABSICHTLICH verschiedene Zweige nehmen -
 * so misst man beide Seiten des Baums und nicht zweimal dieselbe.
 *
 * Die ersten zwei kaufen Hero-Items, der dritte kauft Line-Items
 * (Produktentscheidung vom 28.09.2026). Vorher kannte der Bot War Banner,
 * Trade Seal und Watch Stone ueberhaupt nicht - jedes Match gegen ihn hat
 * damit nur die halbe Kaufentscheidung getestet, und ueber die Zahlen der
 * drei Items liess sich nichts sagen.
 *
 * Bewusst als EIGENE Plaene und nicht in die bestehenden gemischt: So bleiben
 * die acht alten die Vergleichsgruppe. Im Archiv steht dann Hero gegen Linie
 * bei sonst gleichen Bedingungen.
 */
export interface BotBuild {
  id: string;
  heroId: string;
  /** Sprechender Name fuer Logs und Auswertung. */
  name: string;
  /**
   * Reihenfolge der Punkte auf die Grundskills.
   *
   * Ein Eintrag je Punkt. Ist ein Slot schon voll ausgebaut, geht der Punkt an
   * den naechsten Eintrag - so bleibt kein Punkt liegen.
   */
  upgradeOrder: readonly (1 | 2 | 3)[];
  /** Wahl auf Level 8. */
  talentId: string;
  /** Wahl auf Level 10. */
  capstoneId: string;

  /**
   * Reihenfolge der Items (Produktentscheidung vom 25.09.2026).
   *
   * Vorher kaufte der Bot immer dieselben vier, in der Reihenfolge der
   * Itemliste - ein Arcanist legte sich also als Erstes eine Ruestung zu. Jetzt
   * gehoert die Einkaufsliste zum Bauplan: Sie passt zum Hero UND zur
   * Spielweise des Plans, und sie ist damit genauso auswertbar wie der Rest.
   *
   * Gekauft wird von vorne. Sind alle Slots voll, wird das erste Item der
   * Liste weiter ausgebaut, dann das zweite.
   */
  itemOrder: readonly string[];
}

export const BOT_BUILDS: readonly BotBuild[] = [
  // --- Vanguard ---
  {
    id: 'vanguard_roadblock',
    heroId: 'vanguard',
    name: 'Roadblock',
    // Erst zuschlagen und unterbrechen, dann der Flaechenschlag.
    upgradeOrder: [1, 1, 3, 3, 2, 2],
    talentId: 'rock',
    capstoneId: 'bulwark',
    itemOrder: ['bulwark_plate', 'ironhide', 'life_stone', 'march_boots'],
  },
  {
    id: 'vanguard_battering_ram',
    heroId: 'vanguard',
    name: 'Battering Ram',
    // Zuerst der Ansturm: Er sucht die Welle, statt sie zu erwarten.
    upgradeOrder: [2, 2, 1, 1, 3, 3],
    talentId: 'wall',
    capstoneId: 'momentum',
    itemOrder: ['war_blade', 'march_boots', 'bulwark_plate', 'ironhide'],
  },

  // --- Arcanist ---
  {
    id: 'arcanist_firestorm',
    heroId: 'arcanist',
    name: 'Thornstorm',
    upgradeOrder: [1, 1, 3, 3, 2, 2],
    talentId: 'circle',
    capstoneId: 'overload',
    itemOrder: ['rune_stone', 'march_boots', 'bulwark_plate', 'life_stone'],
  },
  {
    id: 'arcanist_ice_field',
    heroId: 'arcanist',
    name: 'Bramblefield',
    upgradeOrder: [2, 2, 3, 3, 1, 1],
    talentId: 'clarity',
    capstoneId: 'chain',
    itemOrder: ['rune_stone', 'bulwark_plate', 'ironhide', 'march_boots'],
  },

  // --- Deadeye ---
  {
    id: 'deadeye_sharpshooter',
    heroId: 'deadeye',
    name: 'Sharpshooter',
    upgradeOrder: [1, 1, 2, 2, 3, 3],
    talentId: 'eagle_eye',
    capstoneId: 'headshot',
    itemOrder: ['war_blade', 'rune_stone', 'march_boots', 'bulwark_plate'],
  },
  {
    id: 'deadeye_sustained_fire',
    heroId: 'deadeye',
    name: 'Sustained Fire',
    upgradeOrder: [3, 3, 1, 1, 2, 2],
    talentId: 'hunter',
    capstoneId: 'second_salvo',
    itemOrder: ['war_blade', 'march_boots', 'rune_stone', 'life_stone'],
  },

  // --- Shade ---
  {
    id: 'shade_whirlwind',
    heroId: 'shade',
    name: 'Whirlwind',
    upgradeOrder: [1, 1, 2, 2, 3, 3],
    talentId: 'sharp_edge',
    capstoneId: 'frenzy',
    itemOrder: ['war_blade', 'march_boots', 'life_stone', 'rune_stone'],
  },
  {
    id: 'shade_shadow_hunt',
    heroId: 'shade',
    name: 'Shadow Hunt',
    upgradeOrder: [3, 3, 2, 2, 1, 1],
    talentId: 'restless',
    capstoneId: 'twin_shadows',
    itemOrder: ['march_boots', 'war_blade', 'ironhide', 'life_stone'],
  },

  /*
   * --- Die Linien-Plaene (Produktentscheidung vom 28.09.2026) ---
   *
   * Alle vier kaufen DIESELBEN drei Line-Items in DERSELBEN Reihenfolge; nur
   * das vierte Item passt zum Hero. Sonst steckten zwei Fragen in einem Plan -
   * "Linie oder Hero?" und "welche Reihenfolge?" - und keine davon waere aus
   * dem Archiv zu beantworten.
   *
   * Das Handelssiegel steht ABSICHTLICH vorne. Es kostet das Anderthalbfache
   * der anderen Items und zahlt sich erst ueber die Restlaufzeit aus. Ob sich
   * das frueh lohnt, ist die Frage, die wir bisher nicht beantworten konnten.
   *
   * Talent und Capstone sind je Hero die GEKREUZTE Kombination: Die beiden
   * geraden Paare stecken schon in den Plaenen darueber, also misst der dritte
   * Plan nebenbei auch noch die Kreuzung.
   */
  {
    id: 'vanguard_supply_line',
    heroId: 'vanguard',
    name: 'Supply Line',
    upgradeOrder: [3, 3, 2, 2, 1, 1],
    talentId: 'wall',
    capstoneId: 'bulwark',
    // Bulwark Plate: Wer vorne steht und seine Linie haelt, muss durchhalten.
    itemOrder: ['trade_seal', 'war_banner', 'watch_stone', 'bulwark_plate'],
  },
  {
    id: 'arcanist_war_economy',
    heroId: 'arcanist',
    name: 'War Economy',
    upgradeOrder: [3, 3, 1, 1, 2, 2],
    talentId: 'clarity',
    capstoneId: 'overload',
    // Rune Stone: Ohne Mana wirkt er gar nichts, egal wie stark die Linie ist.
    itemOrder: ['trade_seal', 'war_banner', 'watch_stone', 'rune_stone'],
  },
  {
    id: 'deadeye_toll_road',
    heroId: 'deadeye',
    name: 'Toll Road',
    upgradeOrder: [2, 2, 3, 3, 1, 1],
    talentId: 'hunter',
    capstoneId: 'headshot',
    // March Boots: Er lebt vom Abstand, und den haelt nur, wer schneller ist.
    itemOrder: ['trade_seal', 'war_banner', 'watch_stone', 'march_boots'],
  },
  {
    id: 'shade_black_market',
    heroId: 'shade',
    name: 'Black Market',
    upgradeOrder: [2, 2, 1, 1, 3, 3],
    talentId: 'restless',
    capstoneId: 'frenzy',
    // March Boots: Tempo ist beim Shade nicht Beiwerk, sondern seine Passive.
    itemOrder: ['trade_seal', 'war_banner', 'watch_stone', 'march_boots'],
  },
];

/** Alle Bauplaene eines Heroes. Der Bot wuerfelt einen davon aus. */
export function getBotBuilds(heroId: string): BotBuild[] {
  return BOT_BUILDS.filter((build) => build.heroId === heroId);
}

export function getBotBuild(id: string): BotBuild | undefined {
  return BOT_BUILDS.find((build) => build.id === id);
}
